import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Camera, X, Loader2 } from 'lucide-react';
import { useEquipmentPickups } from '@/hooks/useEquipmentPickups';

interface EquipmentDialogProps {
  open: boolean;
  onClose: (hasEquipment: boolean) => void;
  timeEntryId: string;
  projectName: string;
  hoursWorked: string;
}

const MAX_PHOTOS = 10;
const MAX_WIDTH = 1200;
const JPEG_QUALITY = 0.8;

const compressImage = (file: File): Promise<File> => {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > MAX_WIDTH) {
        height = (height * MAX_WIDTH) / width;
        width = MAX_WIDTH;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => resolve(new File([blob!], file.name, { type: 'image/jpeg' })),
        'image/jpeg',
        JPEG_QUALITY
      );
    };
    img.src = url;
  });
};

export const EquipmentDialog: React.FC<EquipmentDialogProps> = ({
  open,
  onClose,
  timeEntryId,
  projectName,
  hoursWorked,
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { createPickup, uploadPhotos } = useEquipmentPickups();

  const handleNo = () => {
    resetState();
    onClose(false);
  };

  const handleYes = () => setStep(2);

  const handlePhotosSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_PHOTOS - photos.length;
    const toAdd = files.slice(0, remaining);

    const compressed = await Promise.all(toAdd.map(compressImage));
    const newPreviews = compressed.map((f) => URL.createObjectURL(f));

    setPhotos((prev) => [...prev, ...compressed]);
    setPreviews((prev) => [...prev, ...newPreviews]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (index: number) => {
    URL.revokeObjectURL(previews[index]);
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const pickupId = await createPickup(timeEntryId, description || null, photos.length);
      if (pickupId && photos.length > 0) {
        uploadPhotos(pickupId, photos).catch(console.error);
      }
    } finally {
      setSubmitting(false);
      resetState();
      onClose(true);
    }
  };

  const resetState = () => {
    setStep(1);
    setDescription('');
    previews.forEach(URL.revokeObjectURL);
    setPhotos([]);
    setPreviews([]);
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        {step === 1 && (
          <>
            <DialogHeader>
              <DialogTitle className="text-center">Shift terminé !</DialogTitle>
            </DialogHeader>
            <div className="text-center space-y-4">
              <div className="text-4xl">🔧</div>
              <p className="text-muted-foreground">
                {hoursWorked} travaillées sur {projectName}
              </p>
              <div className="bg-muted rounded-lg p-4">
                <p className="font-semibold mb-3">
                  Y a-t-il des équipements à récupérer ?
                </p>
                <div className="flex gap-3 justify-center">
                  <Button onClick={handleYes}>✓ Oui</Button>
                  <Button variant="secondary" onClick={handleNo}>Non</Button>
                </div>
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <DialogHeader>
              <DialogTitle>Équipements à récupérer</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Textarea
                placeholder="Décrivez les équipements à récupérer..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                onChange={handlePhotosSelected}
              />

              {photos.length < MAX_PHOTOS && (
                <Button
                  variant="outline"
                  className="w-full border-dashed"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Prendre des photos ({photos.length}/{MAX_PHOTOS})
                </Button>
              )}

              {previews.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {previews.map((src, i) => (
                    <div key={i} className="relative aspect-square">
                      <img
                        src={src}
                        alt={`Photo ${i + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        onClick={() => removePhoto(i)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <Button
                className="w-full"
                onClick={handleSubmit}
                disabled={submitting || (!description && photos.length === 0)}
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Envoi...</>
                ) : (
                  'Confirmer'
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
